import { type InjectionKey, provide } from 'vue';
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import type { ZodSchema } from '../../zod';
import { z } from '../../zod';
import type { FormSchema, UseDynamicForm } from '../../types';

export const PROVIDE_FORM = Symbol('PROVIDE_FORM') as InjectionKey<{
  form: ReturnType<typeof useForm>;
  dynamicFormSchema: FormSchema;
}>;

export function useDynamicForm({ formSchema, validationSchema }: UseDynamicForm) {
  const form = useForm({
    validationSchema: buildValidationSchema(formSchema, validationSchema),
  });

  const dynamicFormSchema = formSchema;
  provide(PROVIDE_FORM, { form, dynamicFormSchema });

  return { form, dynamicFormSchema };
}

function buildValidationSchema(formSchema: FormSchema, validationSchema?: ZodSchema) {
  /**
   * Our validation schema is either passed in as a separate object, or under the `validation` key of individual fields
   * If separate keys on fields, we extract it and create a ZodSchema from it.
   */
  const valSchema = validationSchema ?? z.object(Object
    .keys(formSchema)
    .reduce((acc, fieldKey) => ({
      ...acc,
      [fieldKey]: formSchema[fieldKey].validation,
    }), {}));

  const typedValidationSchema = Object.keys(valSchema).length !== 0
    ? toTypedSchema(valSchema as ZodSchema)
    : undefined;

  return typedValidationSchema;
}
